import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProcessSection from '@/components/feature/ProcessSection';
import WhyChooseUs from '@/components/feature/WhyChooseUs';
import ServiceAreas from '@/components/feature/ServiceAreas';
import Testimonials from '@/components/feature/Testimonials';
import FAQSection from '@/components/feature/FAQSection';
import FinalCTA from '@/components/feature/FinalCTA';
import MidPageCTA from '@/components/feature/MidPageCTA';
import HeroSection from '@/pages/home/components/HeroSection';
import TrustBar from '@/pages/home/components/TrustBar';
import AIAnswerBlock from '@/pages/home/components/AIAnswerBlock';

const servicesList = [
  {
    id: 'automotive-tinting',
    icon: 'ri-car-fill',
    title: 'Automotive Tinting',
    tag: 'Most Popular',
    tagline: 'Heat · UV · Privacy · Style',
    desc: 'Professional window tinting for cars, SUVs, and trucks of all makes and models. Dramatically reduces interior heat, blocks 99% UV rays, enhances privacy, and delivers a clean, polished look.',
    benefits: [
      'Up to 60% heat rejection',
      '99% UV ray blockage',
      'Reduces interior glare',
      'Enhanced privacy & style',
      'Fading protection for upholstery',
      'Shatter-resistant film layer',
    ],
    image: 'https://public.readdy.ai/ai/img_res/df55c920ea03f36312bfc733f8175be7.jpg',
  },
  {
    id: 'commercial-tinting',
    icon: 'ri-building-line',
    title: 'Commercial Tinting',
    tag: 'Business Solutions',
    tagline: 'Energy · Privacy · Glare · Aesthetics',
    desc: 'High-performance window film for offices, retail storefronts, warehouses, and commercial buildings. Reduce energy costs, control glare, improve privacy, and upgrade the appearance of any commercial space.',
    benefits: [
      'Significant energy cost reduction',
      'Glare control for workspaces',
      'Enhanced exterior aesthetics',
      'UV protection for merchandise',
      'Improved employee comfort',
      'Security film options available',
    ],
    image: 'https://readdy.ai/api/search-image?query=commercial%20building%20office%20window%20tinting%20professional%20film%20application%20modern%20glass%20storefront%20exterior%20urban%20architecture%20privacy%20film%20installation%20premium%20tint%20Philadelphia%20commercial%20building%20glare%20reduction%20energy%20efficiency&width=900&height=600&seq=sv-comm-d01&orientation=landscape',
  },
  {
    id: 'residential-tinting',
    icon: 'ri-home-4-line',
    title: 'Residential Tinting',
    tag: 'Home Solutions',
    tagline: 'UV · Heat · Privacy · Energy',
    desc: 'Window film solutions for homes — protect your family from UV exposure, reduce heat build-up, save on energy bills, and preserve furniture, flooring, and artwork from sun fading.',
    benefits: [
      'Up to 80% UV ray reduction',
      'Keeps rooms cooler naturally',
      'Protects furniture and flooring',
      'Reduces energy cooling costs',
      'Maintains natural light & view',
      'Privacy without dark appearance',
    ],
    image: 'https://readdy.ai/api/search-image?query=residential%20home%20window%20tinting%20film%20installation%20modern%20house%20bright%20living%20room%20natural%20light%20privacy%20UV%20protection%20sun%20reduction%20professional%20window%20film%20applied%20clean%20minimal%20interior%20Philadelphia%20home%20residential&width=900&height=600&seq=sv-res-d01&orientation=landscape',
  },
  {
    id: 'ceramic-tinting',
    icon: 'ri-contrast-drop-2-line',
    title: 'Ceramic Tinting',
    tag: 'Premium Grade',
    tagline: 'Maximum Performance · No Signal Block',
    desc: 'Top-tier ceramic window film with the highest heat and UV rejection available. No metal in the film means zero interference with GPS, radio, or phone signals — the ultimate upgrade for discerning clients.',
    benefits: [
      'Superior infrared heat rejection',
      'No GPS or signal interference',
      'Crystal clear visibility',
      'Maximum UV protection',
      'Long-lasting color stability',
      'Reduces eye strain from glare',
    ],
    image: 'https://readdy.ai/api/search-image?query=ceramic%20window%20tint%20film%20premium%20car%20window%20close%20up%20high%20performance%20ceramic%20tinting%20dark%20sleek%20dramatic%20cinematic%20studio%20dark%20background%20high%20contrast%20moody%20automotive%20detail%20professional%20installation%20clear%20visibility&width=900&height=600&seq=sv-cer-d01&orientation=landscape',
  },
  {
    id: 'carbon-fiber-installation',
    icon: 'ri-grid-line',
    title: 'Carbon Fiber Installation',
    tag: 'Performance Film',
    tagline: 'Durability · Non-Reflective · Heat',
    desc: 'Carbon-based window film delivers outstanding heat and infrared rejection without reflectivity. The non-metallic structure means no signal interference and a deep, premium matte-black finish that never fades.',
    benefits: [
      'Superior IR heat rejection',
      'Non-reflective dark finish',
      'No signal interference',
      'Fade-resistant film layer',
      'Long-lasting color stability',
      'Excellent daytime clarity',
    ],
    image: 'https://readdy.ai/api/search-image?query=carbon%20fiber%20window%20tint%20film%20dark%20sleek%20luxury%20car%20side%20window%20cinematic%20lighting%20dark%20moody%20studio%20background%20high%20contrast%20glossy%20paint%20reflection%20professional%20carbon%20tint%20installation%20non-reflective%20finish%20automotive&width=900&height=600&seq=sv-carb-d01&orientation=landscape',
  },
  {
    id: 'window-film',
    icon: 'ri-window-line',
    title: 'Window Film',
    tag: 'Versatile Solutions',
    tagline: 'Automotive · Residential · Commercial',
    desc: 'Versatile window film applications tailored to your specific needs — from decorative and frosted films for privacy to safety and security films that hold glass in place upon impact.',
    benefits: [
      'Decorative & frosted options',
      'Safety and security films',
      'Anti-graffiti film available',
      'Solar control and privacy',
      'Custom cut to any dimension',
      'Automotive and flat glass',
    ],
    image: 'https://readdy.ai/api/search-image?query=window%20film%20application%20various%20uses%20professional%20installation%20privacy%20frosted%20decorative%20safety%20film%20modern%20architecture%20glass%20window%20professional%20installer%20applying%20film%20precision%20clean%20modern%20space&width=900&height=600&seq=sv-film-d01&orientation=landscape',
  },
];

const serviceAIQA = [
  {
    q: 'What window tinting films does TINTSPRO X offer?',
    a: 'TINTSPRO X offers automotive tinting, commercial tinting, residential tinting, ceramic tinting, carbon fiber installation, and window film — each option tailored to different performance, aesthetic, and budget requirements.',
  },
  {
    q: 'How long does window tinting take?',
    a: 'Most full-vehicle automotive tint installations are completed in 2–4 hours. Residential projects depend on the number of windows. Commercial tinting is scoped and scheduled per project. Same-day availability for most automotive jobs.',
  },
  {
    q: 'What is the difference between ceramic and carbon tinting?',
    a: 'Ceramic tinting uses ceramic particles for maximum heat rejection and clarity with no signal interference. Carbon tinting uses carbon particles for excellent heat rejection and a deep non-reflective finish. Both are premium upgrades over standard dyed film.',
  },
  {
    q: 'Can residential window film reduce energy costs?',
    a: 'Yes. Residential window tinting blocks up to 80% of solar heat, which significantly reduces the load on air conditioning systems. Most homeowners notice a reduction in cooling costs and improved room comfort year-round.',
  },
];

const serviceFAQs = [
  {
    q: 'How long does window tinting last?',
    a: 'Quality window tint film typically lasts 5–10 years depending on film type and care. Carbon and ceramic tints last longer than standard dyed films and maintain heat rejection performance better over time without fading or bubbling.',
  },
  {
    q: 'Is window tinting legal in Pennsylvania?',
    a: 'Pennsylvania law requires front side windows to allow at least 70% light transmission. Rear side windows and back windshield can be tinted to any level. TINTSPRO X ensures all automotive installations comply with Pennsylvania tint laws.',
  },
  {
    q: 'Do you offer commercial window tinting for large buildings?',
    a: 'Yes. TINTSPRO X handles commercial window tinting for offices, storefronts, warehouses, and large buildings throughout Philadelphia. Projects are scoped individually, and free consultations are available for commercial property owners.',
  },
  {
    q: 'What window film options are available for homes?',
    a: 'TINTSPRO X offers solar control film, privacy film, decorative frosted film, and safety security film for residential properties. Each option provides different levels of heat rejection, UV protection, and privacy while preserving natural light.',
  },
];

function ServiceDetailCard({ svc, index }: { svc: typeof servicesList[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`flex flex-col ${index % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'} overflow-hidden rounded-2xl border border-white/8 transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="w-full lg:w-[45%] h-[280px] lg:h-auto overflow-hidden shrink-0 relative">
        <img src={svc.image} alt={svc.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#080808]/40" />
        {svc.tag && (
          <div className="absolute top-5 left-5">
            <span className="bg-[#AAFF00] text-[#080808] text-[11px] font-sans font-semibold px-3 py-1.5 rounded-full">
              {svc.tag}
            </span>
          </div>
        )}
      </div>
      <div className="flex-1 bg-[#0f0f0f] p-8 md:p-12 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#AAFF00]/10 border border-[#AAFF00]/15">
            <i className={`${svc.icon} text-[#AAFF00] text-xl`}></i>
          </div>
          <div>
            <h3 className="font-serif text-white text-2xl md:text-3xl font-bold leading-tight">{svc.title}</h3>
            <p className="text-[#AAFF00] text-xs font-sans font-medium tracking-wide mt-0.5">{svc.tagline}</p>
          </div>
        </div>
        <p className="text-white/50 font-sans text-sm md:text-base leading-relaxed mt-5 mb-7">{svc.desc}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
          {svc.benefits.map((b) => (
            <div key={b} className="flex items-start gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-check-line text-[#AAFF00] text-sm"></i>
              </div>
              <span className="text-white/50 text-sm font-sans leading-snug">{b}</span>
            </div>
          ))}
        </div>
        <a
          href="#estimate"
          className="whitespace-nowrap inline-flex items-center gap-2 bg-[#AAFF00] text-[#080808] text-sm font-sans font-semibold px-6 py-3 rounded-full hover:bg-[#C4FF33] transition-colors cursor-pointer w-fit"
        >
          Get a Quote for {svc.title}
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-sm"></i>
          </div>
        </a>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  return (
    <div className="bg-[#080808]">
      <Navbar />
      <HeroSection
        eyebrow="Window Tinting Services · Philadelphia, PA"
        title="Professional Window Tinting Services"
        subtitle="Automotive, residential, and commercial window tinting — premium film, precision installation, and expert results for every project in Philadelphia."
        primaryCTA="Get a Service Quote"
        imageSrc="https://readdy.ai/api/search-image?query=luxury%20black%20car%20dark%20tinted%20windows%20sleek%20automotive%20side%20view%20professional%20window%20tinting%20result%20dramatic%20cinematic%20studio%20dark%20background%20high%20contrast%20moody%20deep%20reflections%20premium%20tinting%20shop%20Philadelphia%20professional%20result%20finished%20vehicle&width=1920&height=1080&seq=svc-hero-tint-001&orientation=landscape"
      />
      <TrustBar />

      {/* Service Detail Section */}
      <section className="bg-[#080808] py-28 md:py-36 px-6 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-[1px] bg-[#AAFF00]" />
              <span className="text-[#AAFF00] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">What We Offer</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl xl:text-6xl font-bold text-white leading-[1.05] mb-4">
              Window Tinting Services
            </h2>
            <p className="text-white/45 font-sans text-base max-w-xl">
              Every service uses professional-grade film, precision installation, and a meticulous finish — no shortcuts, ever.
            </p>
          </div>
          <div className="space-y-5">
            {servicesList.map((svc, i) => (
              <ServiceDetailCard key={svc.id} svc={svc} index={i} />
            ))}
          </div>
        </div>
      </section>

      <MidPageCTA
        heading="Have a Vehicle, Home, or Business in Mind?"
        subheading="Get a free, no-obligation quote tailored to your tinting needs."
        ctaLabel="Request Free Quote"
        variant="gold"
      />
      <AIAnswerBlock heading="Window Tinting Services Explained" items={serviceAIQA} />
      <ProcessSection />
      <WhyChooseUs
        title="Tinting Specialists.\nNot a General Shop."
        subtitle="We focus exclusively on window tinting — automotive, residential, and commercial. That focus is why our results consistently exceed expectations."
      />
      <MidPageCTA
        heading="Philadelphia's Professional Window Tinting Studio"
        subheading="Serving all vehicle types and properties across the Philadelphia region."
        ctaLabel="Book Your Appointment"
        variant="dark"
      />
      <ServiceAreas />
      <Testimonials />
      <FAQSection heading="Window Tinting FAQs" items={serviceFAQs} />
      <FinalCTA
        heading="Get Your Windows&#10;Tinted Today"
        subheading="Tell us your vehicle, home, or business — we'll design the perfect tinting solution."
        buttonLabel="Request a Service Quote"
      />
      <Footer />
    </div>
  );
}
